import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FileOut,
  isAbortError,
  isApiError,
  UploadErrorCode,
  type UploadPurpose,
  type UploadValidationError,
  uploadFile,
  validateUploadFile,
} from '@/shared/api';
import { formatFileSize } from '@/shared/lib';
import { getErrorMessage } from '../errorMessage';

export type FileUploadStatus = 'idle' | 'uploading' | 'done' | 'error';

export interface FileUploadState {
  status: FileUploadStatus;
  /** Выбранный файл (для имени и размера в строке файла). */
  file: File | null;
  /** Доля загруженного, 0…1. */
  progress: number;
  /** Проверенный сервером файл: `upload_id` — значение `*_file_id`. */
  result: FileOut | null;
  /** Локальное превью изображения (object URL) — пока сервер не отдал ссылку. */
  previewUrl: string | null;
  /** Сообщение об ошибке на языке интерфейса. */
  error: string | null;
}

const IDLE: FileUploadState = {
  status: 'idle',
  file: null,
  progress: 0,
  result: null,
  previewUrl: null,
  error: null,
};

export interface UseFileUploadOptions {
  purpose: UploadPurpose;
  /**
   * Файл загружен и проверен. Может вернуть промис (например, сохранение `*_file_id` в карточке):
   * его ошибка показывается как ошибка загрузки (422 — сообщение поля).
   */
  onUploaded?: (file: FileOut, source: File) => unknown;
}

interface UploadVariables {
  file: File;
  signal: AbortSignal;
  onProgress: (fraction: number) => void;
}

function createPreview(file: File): string | null {
  if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(file);
}

function releasePreview(url: string | null): void {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

/**
 * Загрузка одного файла (COM-13): проверка типа и размера на клиенте по правилам назначения,
 * `POST /admin/uploads` → PUT на presigned URL с прогрессом → `complete`. Новый выбор файла или
 * `cancel()` прерывает текущую загрузку; ошибки — локализованным текстом для поля.
 */
export function useFileUpload({ purpose, onUploaded }: UseFileUploadOptions) {
  const [state, setState] = useState<FileUploadState>(IDLE);
  const attemptRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const previewRef = useRef<string | null>(null);
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  }, [onUploaded]);

  const mutation = useMutation({
    mutationFn: ({ file, signal, onProgress }: UploadVariables) =>
      uploadFile(file, purpose, { signal, onProgress }),
    meta: { errorToast: false },
  });
  const { mutateAsync } = mutation;

  const { validationMessage, errorMessage } = useUploadMessages();

  const stop = useCallback(() => {
    attemptRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    releasePreview(previewRef.current);
    previewRef.current = null;
  }, []);

  const start = useCallback(
    async (file: File): Promise<FileOut | null> => {
      stop();
      const attempt = attemptRef.current;
      const invalid = validateUploadFile(file, purpose);
      if (invalid) {
        setState({ ...IDLE, status: 'error', file, error: validationMessage(invalid) });
        return null;
      }
      const controller = new AbortController();
      controllerRef.current = controller;
      const previewUrl = createPreview(file);
      previewRef.current = previewUrl;
      setState({ ...IDLE, status: 'uploading', file, previewUrl });
      const isCurrent = () => attempt === attemptRef.current;
      try {
        const result = await mutateAsync({
          file,
          signal: controller.signal,
          onProgress: (progress) => {
            if (isCurrent()) setState((current) => ({ ...current, progress }));
          },
        });
        if (!isCurrent()) return null;
        await onUploadedRef.current?.(result, file);
        if (!isCurrent()) return null;
        setState({ status: 'done', file, progress: 1, result, previewUrl, error: null });
        return result;
      } catch (error) {
        if (!isCurrent() || isAbortError(error)) return null;
        setState((current) => ({
          ...current,
          status: 'error',
          progress: 0,
          error: errorMessage(error),
        }));
        return null;
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [errorMessage, mutateAsync, purpose, stop, validationMessage],
  );

  /** Прервать загрузку и забыть выбранный файл. */
  const reset = useCallback(() => {
    stop();
    setState(IDLE);
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { state, start, reset };
}

/** Локализованные сообщения загрузки: проверка до загрузки и ошибки API/хранилища. */
export function useUploadMessages() {
  const { t, i18n } = useTranslation('form');
  const validationMessage = useCallback(
    (invalid: UploadValidationError): string => {
      if (invalid.code === UploadErrorCode.FileTooLarge) {
        return t('upload.errors.tooLarge', { max: formatFileSize(invalid.maxSize, i18n.language) });
      }
      return t('upload.errors.type', { formats: t(`upload.formats.${invalid.formats}`) });
    },
    [i18n.language, t],
  );

  const errorMessage = useCallback(
    (error: unknown): string => {
      if (isApiError(error)) {
        if (error.code === UploadErrorCode.StorageRejected) return t('upload.errors.storage');
        const [first] = error.fieldErrors;
        if (first) return first.message;
      }
      return getErrorMessage(error);
    },
    [t],
  );

  return { validationMessage, errorMessage };
}
